import { useTranslation } from 'react-i18next';
import { useAppState } from 'context/AppState';
import Msg from 'assets/icons/msg.svg';
import { trackSummaryFeedback } from 'utils/helpers/amplitude';
import { useReportOverviewData } from './useReportOverviewData';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import {
  StyledHeaderText,
  StyledHeaderSmallText,
  IconBox,
  StyledOsrSummaryText,
  StyledSummaryGeneratedText,
  StyledAISummaryWarningText,
  StyledAISummaryWarningTextUnderlined,
  StyledFeedbackButton,
  StyledSummaryFeedbackContainer,
  StyledFeedbackContentLeft,
  StyledSummaryFeedbackText,
  StyledIcon,
  StyledSkeleton,
  StyledSkeletonLine,
  StyledCOllapseButton,
  StyledSummaryHeaderContainer,
} from './ReportOverview.styles';
import thumbsup_icon from 'assets/icons/thumbsup_icon.svg';
import { useState, useEffect, useRef } from 'react';
import CW_AI_Summary from 'assets/icons/CW_AI_Summary.svg';
import CW_AI_Summary_Icon from 'assets/icons/CW_AI_Summary_Icon.svg';
import copy_clipboard from 'assets/icons/copy_clipboard.svg';
import copied_clipboard from 'assets/icons/copied_clipboard.svg';
import up_arrow from 'assets/icons/keyboard_arrow_up_24px.svg';
import down_arrow from 'assets/icons/keyboard_arrow_down_24px.svg';
import track from 'utils/amplitudeWrapper';
import { UserCategories } from 'base/types/AmplitudeClassifiers';

interface ReportOverviewContentProps {
  isCollapsed: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  isAISummarySupported: boolean;
}

const summaryFeedbackStorageKey = (reportId: string): string => `ai_summary_feedback_${reportId}`;

/** True when this browser already recorded thumbs-up/down for the report's AI summary. */
const hasStoredSummaryFeedback = (reportId?: string): boolean => {
  if (!reportId) {
    return false;
  }
  return Boolean(localStorage.getItem(summaryFeedbackStorageKey(reportId)));
};

interface StaticReportOverviewProps {
  isGermanyBarleyScript: boolean;
  isFranceBarleyScript: boolean;
  isUKScript: boolean;
  reportOverviewDescription: string;
}

const UK_SCRIPT_OVERVIEW_TEXT =
  'This is a sample recommendation generated for your wider location.\nTo tailor your recommendation, please click above.\n\nThe Cropwise Seed Selector recommendations are created using more than 115,000 trials, 1,400,000 individual yield result comparisons and 20 years of weather data observations. More than 20,000 farmers have already received seed recommendations for 87,000 fields and 5.7 million hectares in Europe.';

/** Report overview shown where the AI summary is not supported: static expert copy. */
const StaticReportOverview = ({
  isGermanyBarleyScript,
  isFranceBarleyScript,
  isUKScript,
  reportOverviewDescription,
}: StaticReportOverviewProps): JSX.Element => {
  const [t] = useTranslation();

  const getOverviewText = (): string => {
    if (isFranceBarleyScript) {
      return t(
        'This recommendation was generated as an example for your geographical area. To obtain a personalized recommendation tailored to your fields, click the blue banner above.',
        {
          defaultValue:
            'Cette recommandation a été générée à titre d’exemple pour votre secteur géographique. Pour obtenir une recommandation personnalisée adaptée à vos parcelles, cliquez sur le bandeau bleu ci-dessus.',
        }
      );
    }
    if (isGermanyBarleyScript) {
      return 'Wir haben für Ihre Region eine erste Sortenauswahl zusammengestellt. Grundlage sind über 115.000 Feldversuche und Ertragsdaten aus vergleichbaren Standorten.\n\nFür eine schlagspezifische Empfehlung zeichnen Sie im Cropwise Seed Selector Ihr Feld ein, geben Ihren Aussaattermin an und wählen Ihre agronomischen Anforderungen an die Sorte — etwa Standfestigkeit, Ramularia-Toleranz oder Ertragsstabilität';
    }
    if (isUKScript) {
      return UK_SCRIPT_OVERVIEW_TEXT;
    }
    return t(reportOverviewDescription);
  };

  return (
    <>
      <StyledHeaderText data-testid="seed-info-crop-wise">
        {isGermanyBarleyScript
          ? 'Ihre Sortenempfehlung für Ihren Standort'
          : t('Our Expert Seed Recommendation')}
      </StyledHeaderText>
      <StyledHeaderSmallText>
        <IconBox>
          <img src={Msg} alt="No img found" className="icon-size" />
        </IconBox>{' '}
        {t('Report overview')}
      </StyledHeaderSmallText>
      <StyledOsrSummaryText>{getOverviewText()}</StyledOsrSummaryText>
    </>
  );
};

const ReportOverviewContent = ({
  isCollapsed,
  onCollapseChange,
  isAISummarySupported,
}: ReportOverviewContentProps): JSX.Element => {
  const [t] = useTranslation();
  const {
    apiData: { recommendationCropwise, osrSummary },
  } = useAppState();

  const {
    reportOverviewDescription,
    cropName,
    isGermanyBarleyScript,
    isFranceBarleyScript,
    isUKScript,
  } = useReportOverviewData();
  const [isSummaryHelpful, setIsSummaryHelpful] = useState<boolean | undefined>(undefined);
  const [isInitiallySaved, setIsInitiallySaved] = useState(false);
  const [isDelayPassed, setIsDelayPassed] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Tooltip State & Timer Ref
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  const reportId = recommendationCropwise?.id;

  // Check localStorage on component load to restore previous feedback
  useEffect(() => {
    if (hasStoredSummaryFeedback(reportId)) {
      setIsInitiallySaved(true);
    }
  }, [reportId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDelayPassed(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  // Cleanup tooltip timer on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  const handleTooltipOpenChange = (visible: boolean) => {
    setIsTooltipOpen(visible);
    track('AI summary tooltip open', {
      'Opened by': recommendationCropwise?.email_id.includes('@syngenta.co')
        ? UserCategories.SYNGENTA_USER
        : UserCategories.NON_SYNGENTA_USER,
      recId: reportId,
    });
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    if (visible) {
      tooltipTimerRef.current = setTimeout(() => {
        setIsTooltipOpen(false);
      }, 5000);
    }
  };

  const handleFeedbackClick = (helpful: boolean) => {
    setIsSummaryHelpful(helpful);

    if (reportId) {
      const feedbackKey = summaryFeedbackStorageKey(reportId);
      const feedbackData = {
        helpful,
        timestamp: Date.now(),
        email: recommendationCropwise?.email_id,
        country: recommendationCropwise?.country,
        crop: cropName,
      };
      localStorage.setItem(feedbackKey, JSON.stringify(feedbackData));
    }

    trackSummaryFeedback(
      helpful,
      recommendationCropwise?.email_id,
      recommendationCropwise?.country,
      cropName
    );
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(osrSummary.summary);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 5000);
  };

  if (!isAISummarySupported) {
    return (
      <StaticReportOverview
        isGermanyBarleyScript={isGermanyBarleyScript}
        isFranceBarleyScript={isFranceBarleyScript}
        isUKScript={isUKScript}
        reportOverviewDescription={reportOverviewDescription}
      />
    );
  }

  return (
    <>
      {isAISummarySupported && (
        <StyledSummaryHeaderContainer>
          <StyledSummaryGeneratedText>
            {osrSummary?.status === 'COMPLETED' ? (
              <>
                <img src={CW_AI_Summary} alt="AI Summary" />
                {t('Summarized by Cropwise AI')}
              </>
            ) : (
              <>
                <img src={CW_AI_Summary_Icon} alt="No img found" />
                {isDelayPassed ? t('Just a few moments more...') : t('Preparing Summary...')}
              </>
            )}
          </StyledSummaryGeneratedText>
          <StyledCOllapseButton onClick={() => onCollapseChange(!isCollapsed)}>
            <img
              src={isCollapsed ? down_arrow : up_arrow}
              alt="Toggle Arrow"
              width={12}
              height={10}
            />
          </StyledCOllapseButton>
        </StyledSummaryHeaderContainer>
      )}

      {osrSummary?.status === 'COMPLETED' ? (
        <>
          <StyledOsrSummaryText isCollapsed={isCollapsed}>
            {osrSummary.summary}
          </StyledOsrSummaryText>
          {!isCollapsed && (
            <>
              <StyledAISummaryWarningText data-testid="ai-summary-warning">
                <Tooltip
                  open={isTooltipOpen}
                  onOpenChange={handleTooltipOpenChange}
                  trigger={['click']}
                  placement="topLeft"
                  color="#8b4ae6"
                  getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  overlayInnerStyle={{
                    color: '#ffffff',
                    fontStyle: 'normal',
                    fontWeight: 300,
                    fontSize: '14px',
                    lineHeight: '20px',
                    padding: '12px',
                    borderRadius: '2px',
                    maxWidth: '250px',
                  }}
                  title={t('AI summary tooltip Declaration text')}
                >
                  <StyledAISummaryWarningTextUnderlined isOpen={isTooltipOpen}>
                    {t('Cropwise AI can make mistakes')}
                  </StyledAISummaryWarningTextUnderlined>
                </Tooltip>
                . {t('Always verify the information.')}
              </StyledAISummaryWarningText>
              <StyledSummaryFeedbackContainer>
                <StyledFeedbackContentLeft>
                  {isSummaryHelpful === undefined && !isInitiallySaved ? (
                    <>
                      <StyledFeedbackButton onClick={() => handleFeedbackClick(true)}>
                        <img src={thumbsup_icon} alt="thumbsup_icon" />{' '}
                      </StyledFeedbackButton>{' '}
                      <StyledFeedbackButton
                        onClick={() => handleFeedbackClick(false)}
                        style={{ rotate: '180deg' }}
                      >
                        <img src={thumbsup_icon} alt="thumbsdown_icon" />{' '}
                      </StyledFeedbackButton>{' '}
                    </>
                  ) : (
                    <StyledSummaryFeedbackText>
                      {!isInitiallySaved && t('Thanks for your feedback!')}
                    </StyledSummaryFeedbackText>
                  )}
                </StyledFeedbackContentLeft>

                <StyledIcon>
                  <button
                    onClick={handleCopyToClipboard}
                    aria-label="Copy to clipboard"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <img
                      src={isCopied ? copied_clipboard : copy_clipboard}
                      alt=""
                      style={{
                        transform: isCopied ? 'scale(1.3) translateY(-4px)' : 'scale(1)',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    />
                  </button>
                </StyledIcon>
              </StyledSummaryFeedbackContainer>
            </>
          )}
        </>
      ) : (
        <StyledSkeleton>
          <StyledSkeletonLine />
          <StyledSkeletonLine />
          <StyledSkeletonLine />
        </StyledSkeleton>
      )}
    </>
  );
};

export default ReportOverviewContent;
